import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getBundleById, getEventBundle } from '../lib/firebase.js';
import PortalHeader from '../components/PortalHeader';
import PageHeader from '../components/PageHeader';

export default function PublishedBundleView() {
  const { clubId, eventId, bundleId } = useParams();
  const [bundle, setBundle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    async function load() {
      try {
        let b = null;
        if (eventId) {
          // Event-based path: clubs/{clubId}/events/{eventId}/bundle/{bundleId}
          const version = bundleId.startsWith('v') ? parseInt(bundleId.slice(1)) : parseInt(bundleId);
          if (!isNaN(version)) {
            b = await getEventBundle(clubId, eventId, version);
          }
        }
        if (!b) {
          // Fallback to legacy path
          b = await getBundleById(clubId, bundleId);
        }
        if (!b) {
          setError('Bundle not found.');
        } else {
          setBundle(b);
        }
      } catch (err) {
        setError('Failed to load bundle: ' + err.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [clubId, eventId, bundleId]);

  function formatDate(ts) {
    if (!ts) return '—';
    const date = ts.toDate ? ts.toDate() : new Date(ts.seconds ? ts.seconds * 1000 : ts);
    return date.toLocaleDateString('en-IE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  if (loading) {
    return (
      <div className="page">
        <p>Loading bundle...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page">
        <div className="error-banner">{error}</div>
        <Link to={`/clubs/${clubId}/course-cards`}>Back to course cards</Link>
      </div>
    );
  }

  const cards = bundle.course_cards || [];
  const marks = bundle.marks || [];
  const fleetAssignments = bundle.fleet_assignments || [];

  return (
    <>
      <PortalHeader />
      <div className="page published-view">
        <div className="no-print">
          <PageHeader
            backTo={`/clubs/${clubId}/courses/history`}
            backLabel="Version History"
            title={`${bundle.event_id} — v${bundle.version}`}
          >
            <button className="btn btn-sm" onClick={() => window.print()}>
              Print
            </button>
          </PageHeader>
          <p className="subtitle" style={{ marginTop: '-0.75rem', marginBottom: '1rem' }}>
            Status: <span className="status-badge published">published</span>
            {' '}&middot; Published: {formatDate(bundle.published_at || bundle.created_at)}
            {bundle.source_bundle_id && (
              <>
                {' '}&middot; Source: <code>{bundle.source_bundle_id}</code>
              </>
            )}
          </p>
        </div>

      <section className="section print-section">
        <h2>
          Course Cards
          {cards.length > 0 && <span className="badge">{cards.length}</span>}
        </h2>
        {cards.length === 0 ? (
          <p className="empty-state">No course cards in this bundle.</p>
        ) : (
          cards.map((card) => {
            const fleet = fleetAssignments.find(
              (fa) => fa.course_card_id === card.id
            );
            const courses = card.courses || [];
            return (
              <div key={card.id} className="published-card-section">
                <h3 className="card-name-row">
                  {card.name || card.id}
                  {card.vhf && <span className="vhf-tag">VHF {card.vhf}</span>}
                  {fleet && <span className="fleet-tag">{fleet.fleet_id}</span>}
                  <span className="badge">{courses.length}</span>
                </h3>
                {card.footnotes && <p className="card-footnotes">{card.footnotes}</p>}
                {courses.length === 0 ? (
                  <p className="empty-state">No courses on this card.</p>
                ) : (
                  <div className="course-grid">
                    {courses.map((c, i) => (
                      <div key={c.id || i} className="course-card-print">
                        <div className="card-header-row">
                          <strong>{c.name || `${c.directionCode}${c.courseNumber}`}</strong>
                        </div>
                        <div className="card-detail">
                          Bearing: {c.bearing != null ? `${c.bearing}°` : '—'}
                          {' '}&middot; Marks: {(c.markIds || []).length}
                        </div>
                        <div className="card-marks">
                          {(c.markIds || []).map((mId, j) => (
                            <span key={j} className="mark-item">
                              {mId}
                              {c.markDirections && c.markDirections[j] && (
                                <small> ({c.markDirections[j]})</small>
                              )}
                              {j < c.markIds.length - 1 && ' → '}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })
        )}
      </section>

      <section className="section print-section">
        <h2>
          Marks
          {marks.length > 0 && <span className="badge">{marks.length}</span>}
        </h2>
        {marks.length > 0 && (
          <div className="marks-grid">
            {marks.map((m) => (
              <div key={m.id} className="mark-chip">
                <strong>{m.id}</strong> {m.name}
                {m.appearance && (
                  <small className="mark-appearance"> — {m.appearance}</small>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
      </div>
    </>
  );
}
